import { ClientOnly } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { Suspense, lazy } from "react"
import FormControl from "./FormControl"
import { Field } from "@/ui/components/ui/field"
import { Label } from "@/ui/components/ui/label"
import { Skeleton } from "@/ui/components/ui/skeleton"
import FieldDescriptionOrError from "@/ui/composed/FieldDescriptionOrError"
import { cn } from "@/ui/lib/utils"

// MDXEditor is browser-only (Lexical touches the DOM at import time), so
// it is split into its own chunk and rendered behind ClientOnly — the
// SSR/prerender pass never loads it. Shared with the admin evaluation
// editor (src/form/MarkdownEditor.tsx).
const MarkdownEditor = lazy(() => import("./MarkdownEditor"))

type Props = {
  readonly name: string
  readonly label?: ReactNode
  readonly helperText?: string
  readonly placeholder?: string
  readonly required?: boolean
}

function EditorFallback() {
  return <Skeleton className="h-64 w-full rounded-md" />
}

/**
 * A markdown field backed by MDXEditor, bound to the surrounding form via
 * FormControl. Same limited toolset as the evaluation editor.
 */
export default function FormMarkdownEditor({
  name,
  label,
  helperText,
  placeholder,
  required,
}: Props) {
  return (
    <FormControl name={name} required={required} emptyAsString>
      {({ field, error }) => (
        <Field className="flex flex-col gap-1.5">
          {!!label && (
            <Label
              htmlFor={name}
              className={cn(required && "after:content-['*']")}
            >
              {label}
            </Label>
          )}
          <ClientOnly fallback={<EditorFallback />}>
            <Suspense fallback={<EditorFallback />}>
              <MarkdownEditor
                markdown={typeof field.value === "string" ? field.value : ""}
                readOnly={false}
                placeholder={placeholder}
                onChange={field.onChange}
              />
            </Suspense>
          </ClientOnly>
          <FieldDescriptionOrError description={helperText} error={error} />
        </Field>
      )}
    </FormControl>
  )
}
